import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import DispatchService from './DispatchService';

// Public, phone-first page a buyer (คนกด) reaches from the daily LINE card or their personal
// link: orders waiting for a Japan tracking number, one at a time. The order is already chosen,
// so the screenshot (which shows no item name) cannot land on the wrong row. The button opens the
// order in the buyer's own Mercari app; the system never touches their account.
// The token in the URL is the key. Thai, because buyers use it.

const MODE = { sea: { label: 'เรือ', cls: 'bg-blue-600' }, air: { label: 'แอร์', cls: 'bg-orange-500' } };
const cardClass = 'bg-white rounded-2xl shadow p-5 max-w-sm w-full';
const mainButton = 'w-full rounded-xl py-3.5 text-lg font-semibold text-white bg-green-600 disabled:opacity-40';
const plainButton = 'w-full rounded-xl py-3 text-base text-gray-700 border border-gray-300 disabled:opacity-40';

// Same rule as the backend: Yamato, Japan Post and Sagawa end 12-13 digit numbers with (the rest mod 7).
const checkFails = (digits) => /^\d{12,13}$/.test(digits) && Number(digits.slice(0, -1)) % 7 !== Number(digits.slice(-1));
const pretty = (n) => (n && /^\d{12}$/.test(n) ? n.replace(/(\d{4})(?=\d)/g, '$1-') : n);
const whenText = (iso) => new Date(iso).toLocaleString('th-TH', {
  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Bangkok',
});
// What the backend reader takes. Listing them (not image/*) makes iPhones hand over HEIC photos as JPEG.
const PICTURE_TYPES = 'image/png,image/jpeg,image/webp,image/gif';
const openText = { normal: 'เปิดหน้าซื้อขาย', shop: 'เปิดหน้าคำสั่งซื้อ', purchases: 'เปิดรายการที่ซื้อ' };

const Shell = ({ children }) => (
  <div className="min-h-screen bg-gray-100 flex items-start justify-center p-4">
    <div className={cardClass}>{children}</div>
  </div>
);

const Item = ({ item, small = false }) => {
  const mode = MODE[item.mode];
  return (
    <>
      {item.imageUrl
        ? <img src={item.imageUrl} alt="" className={`${small ? 'w-20 h-20' : 'w-40 h-40'} object-cover rounded-xl mx-auto`} />
        : null}
      <div className="flex items-center justify-between mt-3">
        <span className="text-2xl font-bold">¥{item.amountJpy.toLocaleString()}</span>
        {mode ? <span className={`text-white font-bold rounded-lg px-4 py-1 ${mode.cls}`}>{mode.label}</span> : null}
      </div>
      <p className="text-sm text-gray-500 mt-1">กดเมื่อ {whenText(item.orderedAt)}{item.qty > 1 ? ` · ${item.qty} ชิ้น` : ''}</p>
    </>
  );
};

export default function DispatchTrackingPage() {
  const { token } = useParams();
  const [items, setItems] = useState(null);
  const [closed, setClosed] = useState(null);
  const [skipped, setSkipped] = useState([]); // snoozed on this visit
  const [doneCount, setDoneCount] = useState(0);
  const [pickedId, setPickedId] = useState(null); // chosen from the photo grid
  const [screen, setScreen] = useState('item'); // item | enter | oldPhoto | confirm | all
  const [digits, setDigits] = useState('');
  const [fromPhoto, setFromPhoto] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [photoRead, setPhotoRead] = useState(false); // a screenshot of this order was read: typing corrects it
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const openedAt = useRef(null);

  // Only the first load closes the page; a failed refresh keeps the list the buyer has.
  const load = useCallback(async (first = false) => {
    try {
      const res = await DispatchService.getMyTracking(token);
      setItems(res.data.items);
    } catch (err) {
      if (first) setClosed(err?.response?.data?.message || 'เปิดหน้านี้ไม่ได้ ลองใหม่อีกครั้ง');
    }
  }, [token]);

  useEffect(() => { load(true); }, [load]);

  const queue = (items || []).filter((i) => i.ready && !i.snoozedUntil && !skipped.includes(i.orderId));
  const current = (items || []).find((i) => i.orderId === pickedId) || queue[0] || null;

  const reset = () => {
    setDigits('');
    setFromPhoto(false);
    setPendingFile(null);
    setPhotoRead(false);
    setNeedsConfirm(false);
    setError(null);
    openedAt.current = null;
  };

  const finish = (orderId) => {
    setItems((list) => list.filter((i) => i.orderId !== orderId));
    setDoneCount((n) => n + 1);
    setPickedId(null);
    reset();
    setScreen('item');
  };

  // The order was finished or cancelled elsewhere: drop everything typed for it and go back to
  // the list, so nothing meant for it can be confirmed onto the next order. Returns true then.
  const failed = (err, fallback) => {
    const message = err?.response?.data?.message || fallback;
    const gone = err?.response?.status === 409 && /มีเลขพัสดุแล้ว|ยกเลิก/.test(message);
    if (gone) {
      reset();
      setPickedId(null);
      setScreen('item');
      load();
    }
    setError(message);
    return gone;
  };

  // Owner 2026-11-03: no self-service undo on the page (a list of today's "no number" taps read as
  // one more thing to do). A slip is fixed by an admin; the page just says so.
  const helpNote = <p className="text-xs text-gray-500 text-center mt-5">กดผิดหรือมีปัญหา ติดต่อแอดมิน</p>;

  const save = async (body) => {
    try {
      setBusy(true);
      setError(null);
      const res = await DispatchService.saveMyTracking(token, current.orderId, body);
      if (res.data.needsConfirm) {
        setNeedsConfirm(true);
        setScreen('confirm');
      } else {
        finish(current.orderId);
      }
    } catch (err) {
      failed(err, 'บันทึกไม่สำเร็จ ลองใหม่อีกครั้ง');
    } finally {
      setBusy(false);
    }
  };

  const typed = (value) => {
    setDigits(value.replace(/[^0-9a-zA-Z]/g, '').slice(0, 20));
    setFromPhoto(false);
    setNeedsConfirm(false);
    setError(null);
  };

  // No jump while typing: 13-digit numbers pass 12 on the way. A failed check digit asks, never blocks.
  const typedDone = () => {
    setNeedsConfirm(checkFails(digits));
    setScreen('confirm');
  };

  const read = async (file) => {
    try {
      setBusy(true);
      setError(null);
      const res = await DispatchService.readMyScreenshot(token, current.orderId, file);
      setPhotoRead(true);
      if (!res.data.trackingJp) {
        // Past the read limit the picture is kept unread: the buyer types the number from it.
        setError(res.data.readSkipped ? 'แนบรูปแล้ว พิมพ์เลขที่เห็นในรูป' : 'อ่านเลขจากรูปไม่ได้ พิมพ์เลขที่เห็นในรูป');
        setScreen('enter');
        return;
      }
      setDigits(res.data.trackingJp);
      setFromPhoto(true);
      setNeedsConfirm(!res.data.checkOk);
      setScreen('confirm');
    } catch (err) {
      if (!failed(err, 'อ่านรูปไม่สำเร็จ ลองแนบรูปใหม่')) setScreen('enter');
    } finally {
      setBusy(false);
    }
  };

  // A picture taken before the page was opened may belong to another order: ask, never block,
  // because a buyer following a Mercari notification may have taken it first.
  const pick = (file) => {
    if (!file) return;
    if (!PICTURE_TYPES.split(',').includes(file.type)) { setError('ใช้รูป PNG หรือ JPG'); return; }
    if (openedAt.current && file.lastModified < openedAt.current) {
      setPendingFile(file);
      setScreen('oldPhoto');
      return;
    }
    read(file);
  };

  const skip = async () => {
    try {
      setBusy(true);
      await DispatchService.snoozeMyTracking(token, current.orderId);
      setSkipped((ids) => [...ids, current.orderId]);
      setPickedId(null);
      reset();
      setScreen('item');
    } catch (err) {
      failed(err, 'บันทึกไม่สำเร็จ ลองใหม่อีกครั้ง');
    } finally {
      setBusy(false);
    }
  };

  if (closed) return <Shell><p className="text-lg font-semibold text-gray-700 text-center py-8">{closed}</p></Shell>;
  if (!items) return <Shell><p className="text-gray-500 text-center py-8">กำลังโหลด...</p></Shell>;

  if (screen === 'all') {
    return (
      <Shell>
        <div className="flex items-center justify-between mb-3">
          <p className="font-semibold">รอเลข {items.length} ชิ้น</p>
          <button type="button" className="text-sm text-gray-600 underline" onClick={() => setScreen('item')}>กลับ</button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {items.map((i) => (
            <button
              type="button"
              key={i.orderId}
              className="rounded-lg overflow-hidden bg-gray-100 text-xs text-gray-700"
              onClick={() => { reset(); setPickedId(i.orderId); setScreen('item'); }}
            >
              {i.imageUrl ? <img src={i.imageUrl} alt="" className="w-full h-20 object-cover" /> : <div className="h-20" />}
              <span className="block py-1">¥{i.amountJpy.toLocaleString()}</span>
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-3">แตะชิ้นที่เพิ่งได้รับแจ้งเตือน</p>
      </Shell>
    );
  }

  if (!current) {
    return (
      <Shell>
        <div className="text-center py-6">
          <p className="text-xl font-bold text-green-700">{doneCount ? 'วันนี้ครบแล้ว' : 'ตอนนี้ไม่มีชิ้นรอเลข'}</p>
          {doneCount ? <p className="text-sm text-gray-500 mt-2">กรอกแล้ว {doneCount} ชิ้น · ปิดหน้านี้ได้เลยครับ</p> : null}
          {items.length > 0 ? <button type="button" className={`${plainButton} mt-5`} onClick={() => setScreen('all')}>ดูรูปทั้งหมด ({items.length})</button> : null}
        </div>
        {helpNote}
      </Shell>
    );
  }

  const position = doneCount + skipped.length + 1;
  const total = doneCount + skipped.length + queue.length;
  const head = pickedId ? null : (
    <div className="mb-3">
      <div className="flex justify-between text-sm">
        <span className="font-semibold">ชิ้นที่ {position} จาก {total}</span>
        <span className="text-gray-500">เสร็จแล้ว {doneCount}</span>
      </div>
      <div className="h-1.5 bg-gray-200 rounded mt-1.5 overflow-hidden">
        <div className="h-full bg-green-600" style={{ width: `${Math.round((position / total) * 100)}%` }} />
      </div>
    </div>
  );

  if (screen === 'oldPhoto') {
    return (
      <Shell>
        {head}
        <p className="rounded-lg bg-amber-100 text-amber-800 text-sm px-3 py-2">รูปนี้แคปก่อนกดเปิดหน้าซื้อขาย</p>
        <p className="mt-4 font-semibold">ใช่รูปของชิ้นนี้แน่ไหม</p>
        <Item item={current} small />
        <div className="grid gap-2 mt-4">
          <button type="button" className={mainButton} disabled={busy} onClick={() => read(pendingFile)}>{busy ? 'กำลังอ่านรูป...' : 'ใช่ ใช้รูปนี้'}</button>
          <button type="button" className={plainButton} disabled={busy} onClick={() => { setPendingFile(null); setScreen('enter'); }}>เลือกรูปใหม่</button>
        </div>
      </Shell>
    );
  }

  if (screen === 'confirm') {
    return (
      <Shell>
        {head}
        <Item item={current} small />
        <p className="text-sm text-gray-500 mt-4">{fromPhoto ? 'ระบบอ่านเลขจากรูปได้แบบนี้' : 'เลขที่ใส่มา'}</p>
        <p className="text-2xl font-mono font-bold text-center border rounded-xl py-3 mt-1 break-all">{pretty(digits)}</p>
        {needsConfirm
          ? <p className="rounded-lg bg-amber-100 text-amber-800 text-sm px-3 py-2 mt-3">เลขนี้ไม่ผ่านการตรวจ ดูให้ตรงกับหน้า Mercari ทุกตัว</p>
          : <p className="text-sm text-gray-500 mt-3">ดูให้ตรงกับหน้า Mercari ก่อนกดยืนยัน</p>}
        {error ? <p className="text-sm text-red-600 mt-2">{error}</p> : null}
        <div className="grid gap-2 mt-4">
          <button type="button" className={mainButton} disabled={busy} onClick={() => save({ trackingJp: digits, confirmed: needsConfirm })}>
            {busy ? 'กำลังบันทึก...' : (needsConfirm ? 'เลขนี้ถูกแน่ ใช้เลขนี้' : 'ยืนยัน ไปชิ้นถัดไป')}
          </button>
          <button type="button" className={plainButton} disabled={busy} onClick={() => { setError(null); setFromPhoto(false); setScreen('enter'); }}>เลขไม่ตรง แก้เอง</button>
        </div>
      </Shell>
    );
  }

  if (screen === 'enter') {
    return (
      <Shell>
        {head}
        <Item item={current} small />
        {/* Admins verify the number from the screenshot, so a number always comes with one.
            Typing only corrects what was read from it. */}
        {photoRead
          ? (
            <>
              <input
                id="tracking-number"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                aria-label="เลขพัสดุ"
                placeholder="พิมพ์เลขพัสดุตามรูป"
                value={digits}
                onChange={(e) => typed(e.target.value)}
                className="w-full h-12 mt-4 rounded-xl border border-gray-400 px-3 text-lg font-mono"
              />
              <p className="text-sm text-red-600 min-h-[1.25rem] mt-1">{error}</p>
              {digits.length >= 8
                ? <button type="button" className={`${mainButton} mb-2`} disabled={busy} onClick={typedDone}>ถัดไป</button>
                : null}
            </>
          )
          : <p className="text-sm text-red-600 min-h-[1.25rem] mt-3">{error}</p>}
        <label className="block rounded-xl border-2 border-dashed border-gray-400 p-4 text-center text-gray-600 cursor-pointer">
          <input type="file" accept={PICTURE_TYPES} className="hidden" disabled={busy} onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
          {busy ? 'กำลังอ่านรูป...' : (photoRead ? 'แนบรูปใหม่' : 'แตะเพื่อแนบรูปหน้าซื้อขายที่มีเลขพัสดุ')}
        </label>
        <div className="grid gap-2 mt-2">
          <button type="button" className={plainButton} disabled={busy} onClick={() => window.confirm('ชิ้นนี้ส่งแล้ว แต่ไม่มีเลขพัสดุ ใช่ไหม') && save({ noTracking: true })}>ชิ้นนี้ไม่มีเลขพัสดุ</button>
          <button type="button" className={plainButton} disabled={busy} onClick={() => { reset(); setScreen('item'); }}>ย้อนกลับ</button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      {head}
      <Item item={current} />
      {current.openKind === 'purchases'
        ? <p className="text-sm text-gray-600 mt-3">ระบบยังไม่มีลิงก์ของชิ้นนี้ เปิดรายการที่ซื้อแล้วหาจากรูปด้านบน</p>
        : null}
      {error ? <p className="text-sm text-red-600 mt-2">{error}</p> : null}
      <div className="grid gap-2 mt-4">
        <a
          href={current.openUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`${mainButton} block text-center`}
          onClick={() => { openedAt.current = Date.now(); setScreen('enter'); }}
        >
          {openText[current.openKind]}
        </a>
        {current.openKind === 'shop'
          ? (
            <a
              href={current.fallbackUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`${plainButton} block text-center`}
              onClick={() => { openedAt.current = Date.now(); setScreen('enter'); }}
            >
              ลิงก์เปิดไม่ได้ เปิดรายการที่ซื้อ
            </a>
          )
          : null}
        <button type="button" className={plainButton} onClick={() => setScreen('enter')}>มีเลขแล้ว แนบรูปเลย</button>
        <button type="button" className={plainButton} disabled={busy} onClick={skip}>ยังไม่ส่ง พรุ่งนี้ค่อยดู</button>
        <button type="button" className={plainButton} onClick={() => setScreen('all')}>ดูรูปทั้งหมด</button>
      </div>
      {helpNote}
    </Shell>
  );
}
